import { useState, useRef, useEffect, useCallback } from 'react';
import { useStore } from '@/store/store';
import { getSeenZones, setSeenZones } from '@/utils/exclusive-popup';
import type { InteractionType } from '@/tracking/amplitude/events/general/exclusives';

export const usePopupBehavior = (zoneUid?: string, onPopupClose?: (interactionType: InteractionType) => void) => {
    const { closePopup } = useStore((state) => state.popup);
    const [fade, setFade] = useState(false);
    const popupRef = useRef<HTMLDivElement>(null);

    const fadeOutAndClose = useCallback(() => {
        const element = popupRef.current;
        if (element) {
            setFade(true);
        } else {
            closePopup();
        }
    }, [closePopup]);

    const handleAnimationEnd = useCallback(
        (event: React.AnimationEvent) => {
            if (event.animationName === 'fadeOutModal' && fade) {
                closePopup();
            }
        },
        [fade, closePopup]
    );

    const handleClose = useCallback(() => {
        onPopupClose?.('x_clicked');
        fadeOutAndClose();

        if (zoneUid) {
            const seenZones = getSeenZones();
            if (!seenZones.includes(zoneUid)) {
                seenZones.push(zoneUid);
                setSeenZones(seenZones);
            }
        }
    }, [onPopupClose, fadeOutAndClose, zoneUid]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (popupRef.current && !popupRef.current.contains(event.target as Node)) {
                onPopupClose?.('outside_frame_clicked');
                fadeOutAndClose();

                if (zoneUid) {
                    const seenZones = getSeenZones();
                    if (!seenZones.includes(zoneUid)) {
                        seenZones.push(zoneUid);
                        setSeenZones(seenZones);
                    }
                }
            }
        };

        document.addEventListener('mousedown', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [fadeOutAndClose, zoneUid, onPopupClose]);

    return {
        fade,
        popupRef,
        fadeOutAndClose,
        handleAnimationEnd,
        handleClose
    };
};
